import { createApi } from "@reduxjs/toolkit/query/react";
import { getAdminAxiosInstance } from "../../../api/Admin";

const axiosBaseQuery =
  () =>
  async ({ params, endpoint, method, body }) => {
    const api = await getAdminAxiosInstance();
    const getParams = (key) => {
      if (params?.[key]) {
        return `${key}=${params[key]}&`;
      } else {
        return "";
      }
    };
    if (method === "get") {
      try {
        const response = await api[method](
          `${endpoint}?${getParams("start")}${getParams("end")}filter=${
            params?.filter || "all"
          }&${getParams("sort")}sort_order=${
            params?.sort_order || "desc"
          }&per_page=${params?.page_size || "20"}&${getParams("search")}page=${
            params?.page || ""
          }`,
          body
        );
        return { data: response.data };
      } catch (axiosError) {
        let err = axiosError;
        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
        };
      }
    } else {
      try {
        const response = await api[method](
          `${endpoint}${getParams("start")}${getParams("end")}${getParams(
            "sort"
          )}${getParams("search")}`,
          body
        );
        return { data: response.data };
      } catch (axiosError) {
        let err = axiosError;
        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
        };
      }
    }
  };

export const cms = createApi({
  reducerPath: "cmsApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    "CMS",
    "Privacy Policy",
    "Basic Data",
    "Edit Data",
    "CustomNotification",
    "CustomNotificationBasicData",
    "NotificationDelivery",
    "Subscription Access",
  ],
  endpoints: (builder) => ({
    getPrivacyPolicyListData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/app-settings/index",
      }),
      providesTags: ["Privacy Policy"],
    }),
    updatePrivacyPolicyTableHeadData: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/user-field/store`,
      }),

      invalidatesTags: ["Privacy Policy"],
    }),
    getComponentBasicData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/settings/basic-data",
      }),
      providesTags: ["Basic Data"],
      keepUnusedDataFor: 1,
    }),
    getComponentEditData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/settings/edit-home-component",
      }),
      providesTags: ["Edit Data"],
    }),
    //custom notification starts here

    getCustomNotification: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/notification/index",
      }),
      providesTags: ["CustomNotification"],
    }),
    getCustomNotificationBasicData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/notification/basic-data",
      }),
      providesTags: ["CustomNotificationBasicData"],
    }),
    updateCustomNotificationHeadData: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/user-field/store`,
      }),

      invalidatesTags: ["CustomNotification"],
    }),

    sendCustomNotification: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/notification/send-notification`,
      }),

      invalidatesTags: ["CustomNotification"],
    }),

    // Per-notification delivery report: header card + summary strip.
    // The id goes in the path, not params: axiosBaseQuery rebuilds GET query
    // strings from a fixed whitelist (start, end, filter, sort, sort_order,
    // page_size, search, page) and silently drops everything else. The status
    // filter rides on `filter` for the same reason.
    getNotificationDeliverySummary: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `/notification/delivery-summary/${values.notification_id}`,
      }),
      providesTags: ["NotificationDelivery"],
    }),
    // Paginated per-user delivery rows.
    getNotificationDeliveryReport: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: `/notification/delivery-report/${values.notification_id}`,
      }),
      providesTags: ["NotificationDelivery"],
    }),
    updateNotificationDeliveryHeadData: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/user-field/store`,
      }),

      invalidatesTags: ["NotificationDelivery"],
    }),
    //custom notification ends her
    getAiSettingsListData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/ai-setting/list",
      }),
      providesTags: ["Ai Settings"],
    }),
    updateAisettingsTableHeadData: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/user-field/store`,
      }),

      invalidatesTags: ["Ai Settings"],
    }),
    getSubscriptionAccessListData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/subscription-setting/list",
      }),
      providesTags: ["Subscription Access"],
    }),
    updateSubscriptionAccessTableHeadData: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/user-field/store`,
      }),
      invalidatesTags: ["Subscription Access"],
    }),
    getYearlyRecapListData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/recap-theme/list",
      }),
      providesTags: ["yearly-recap"],
    }),
    updateYearlyRecapTableHeadData: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/user-field/store`,
      }),

      invalidatesTags: ["yearly-recap"],
    }),
    updateRecapStatus: builder.mutation({
      query: (params) => ({
        method: "post",
        endpoint: `/recap-theme/change-status?id=${params.id}&status=${params.status}`,
      }),
    }),
  }),
});

export const {
  useGetComponentBasicDataQuery,
  useGetComponentEditDataQuery,
  useGetPrivacyPolicyListDataQuery,
  useUpdatePrivacyPolicyTableHeadDataMutation,
  useGetCustomNotificationQuery,
  useUpdateCustomNotificationHeadDataMutation,
  useGetCustomNotificationBasicDataQuery,
  useSendCustomNotificationMutation,
  useGetNotificationDeliverySummaryQuery,
  useGetNotificationDeliveryReportQuery,
  useUpdateNotificationDeliveryHeadDataMutation,
  useGetAiSettingsListDataQuery,
  useUpdateAisettingsTableHeadDataMutation,
  useGetYearlyRecapListDataQuery,
  useUpdateYearlyRecapTableHeadDataMutation,
  useUpdateRecapStatusMutation,
  useGetSubscriptionAccessListDataQuery,
  useUpdateSubscriptionAccessTableHeadDataMutation,
} = cms;
